import {computed, inject, Injectable, signal} from '@angular/core'
import {Calculus, kapitalForsakringar, livforsakring, weightedDiscount} from './calculus/calculus'
import {Configuration} from './configuration'
import {IDecision, IItem, TApproval} from '../0-application/types'
import {PrivateBankingAccounts} from '../0-application/shared'

@Injectable({
  providedIn: 'root'
})
export class Profit {
  /**
   *  These are the items used for the end report, can be
   *  set by outsiders.
   */
  public motivation = signal<string>('')
  public horizon = signal<number | null>(null)
  public approvalStatus = signal<TApproval>('created')
  public approverComment = signal<string | null>(null)
  /**
   * We need to think about what this means. What about
   * private loans?
   */
  public discount = computed(() => {
    return weightedDiscount(this.calculus.newLoans())
  })
  /**
   * The profit of savings is counted as a percentage of the
   * amounts on the whitelisted savings accounts.
   *
   * e-spar and Österlen is counted as sum of investments * 0.0129?
   * Fixed interest accounts are 0.005 * amount 1000 sek => 5 kronors.
   *
   */
  public readonly profitOfSavingsAccounts = computed(() => {
    return this.calculus.otherAccounts()
      .filter(a => a.active() && this.countedProducts.has(a.product()))
      .reduce((sum, account) => {
        const interest = this.countedProducts.get(account.product())! / 100
        const profit = account.balance() * interest
        return sum + profit
      }, 0)
  })

  /**
   * Monthly savings of Kapitalspar = Monthly amount * 36 * 0.0035
   * Monthly savings of Abasec/Investments = Monthly amount * 36 * 0,0055
   */
  public profitOfSavingsAutogiro = computed(() => {
    return this.calculus.monthlyInvestments()
      .filter(a => a.active)
      .reduce((sum, autogiro) => sum + autogiro.profit(), 0)
  })
  /**
   * According to the Excel 0.0055 of the total investments
   */
  public readonly investmentsProfit = computed(() => {
    return this.calculus.investments()
      .filter(i => i.active())
      .map(i => i.balance())
      .reduce((sum, n) => sum + n, 0) * 0.0055
  })

  public savingsProfit = computed(() =>
    this.investmentsProfit() +
    this.profitOfSavingsAccounts() +
    this.profitOfSavingsAutogiro() +
    this.kapitalForsakringProfit()
  )
  /**
   * Loans
   */
  public propertyLoansProfit = computed(() => {
    return this.calculus.newLoans().concat(this.calculus.currentLoans())
      .filter(l => l.active())
      .reduce((acc, loan) => acc + loan.profit(), 0)
  })

  public privateLoansProfit = computed(() => {
    return this.calculus.privateLoans()
      .filter(l => l.active())
      .reduce((acc, loan) => acc + loan.profit(), 0)
  })

  public loansProfit = computed(() => {
    return this.propertyLoansProfit() + this.privateLoansProfit()
  })
  /**
   * Private banking is 5000 if the customer have
   * private banking. We use both loan takers the
   * total
   */
  public privateBankingProfit = computed(() => {
    return Math.min(this.calculus.otherAccounts()
      .filter(l => l.active())
      .some(l => PrivateBankingAccounts.includes(l.product())) ? 5000 : 0, 5000)
  })

  /**
   * Månadssparande kapitalförsäkringar * 36 * 0.0035 enligt excel.
   * OBS Vi har inte månadsspar ännu
   */
  public kapitalForsakringProfit = computed(() => {
    const k = kapitalForsakringar(this.calculus.insurances())
    return k
      .filter(i => i.active())
      .reduce((a, i) => a + i.balance(), 0) * 0.0035
  })
  /**
   * Livförsäkring = monthly * 0.2 * 12
   */
  public lifeInsuranceProfit = computed(() => {
    const k = livforsakring(this.calculus.insurances())
    return k
      .filter(i => i.active())
      .reduce((a, i) => a + i.monthly(), 0) * 0.2 * 12
  })

  /**
   * Betalskydd belopp * 12 * 0,35
   */

  /**
   * Företagsintjäning, täckningsbidrag företag, contribution margin.
   * Note that it can never exceed 5000
   */
  public corporateProfit = computed(() => {
    return this.calculus.contributions()
      .reduce((a, c) => a + c.balance(), 0)
  })

  /**
   * The items on the "Merprodukter" page.
   * * Livförsäkring
   * * Betalskydd
   * .. yadda yadda, we will have to sort this out later
   * when we know what ends up where.
   */
  public moreProductsProfit = computed(() => {
    return this.lifeInsuranceProfit() +
      this.privateBankingProfit() +
      this.corporateProfit()
  })


  /**
   * Returns true if the total earnings are > 0 this includes
   * all profit.
   */
  public positiveProfit = computed(() => {
    return this.totalProfit() > 0
  })

  public totalProfit = computed(() => {
    return this.loansProfit() + this.savingsProfit() + this.moreProductsProfit()
  })

  /**
   * Can be self approved by user.
   */
  public canApprove = computed(() =>
    this.positiveProfit() &&
    this.privateLoansProfit() >= 0 &&
    this.propertyLoansProfit() >= 0
  )

  private readonly config = inject(Configuration)
  private readonly calculus = inject(Calculus)

  private readonly savingsInterest = this.config.interestSavingsAccount()
  private readonly fixed = this.config.interestFixedInterestSavingsAccount()

  private readonly countedProducts = new Map<string, number>([
    ['i51', this.savingsInterest], // Österlenkonto
    ['i83', this.savingsInterest], // E-sparkonto
    //['i15', 0], // Private banking
    //['i16', 0], // Specialinlångin
    ['i23', this.fixed], // Fasträntekonto
    ['i24', this.fixed / 4], // Fasträntekonto 3 mån
    ['i25', this.fixed / 2], // Fasträntekonto 6 mån
    ['i26', this.fixed],// Fasträntekonto 1år
    ['i27', this.fixed],// Fasträntekonto 1,5 år
    ['i28', this.fixed],// Fasträntekonto 2 år
    ['i29', this.fixed],// Fasträntekonto 3 år
    ['i30', this.fixed],// Fasträntekonto 4 år
    ['i31', this.fixed],// Fasträntekonto 5 år
    ['i32', this.fixed / 4],// Fasträntekonto 3 mån auto
    ['i84', this.fixed] // Häst
  ])

  public setCustomer(customer: IItem) {
    this.motivation.set(customer.decision?.motivation ?? '')
    this.horizon.set(customer.decision?.horizon ?? null)
    this.approverComment.set(customer.decision?.approverComment ?? null)
    this.approvalStatus.set(customer.approvalStatus)
  }

  public getCustomer(): IDecision {
    return {
      motivation: this.motivation(),

      horizon: this.horizon(),

      discounts: [this.discount()],

      loanSummaries: this.calculus.loanSummaries(),

      approverComment: this.approverComment(),

      profit: {
        total: this.totalProfit(),
        loans: {
          propertyLoans: this.propertyLoansProfit(),
          privateLoans: this.privateLoansProfit(),
          total: this.loansProfit()
        },
        savings: {
          investments: this.investmentsProfit(),
          savings: 0,
          total: this.savingsProfit()
        },
        moreProducts: {
          contributionMargin: this.corporateProfit(),
          total: this.moreProductsProfit()
        }
      }
    }
  }

  /**
   * Clear all lists as needed.
   */
  public reset() {
    this.horizon.set(null)
    this.motivation.set('')
    this.approvalStatus.set('created')
  }
}
